import { DATASOURCE } from 'Constants'
import { splitNameAndVersion, cmpVersions } from 'helpers/device_helper'

const initialState = {
  raw: {},
  durationSeries: [],
  durationCategories: [],
  topConferencesSeries: [],
  topConferencesCategories: [],
  agentSeries: [],
  agentDrilldown: [],
  agentCategories: [],
  endReasonSeries: [],
  activeCallsPerHourSeries: [],
  timeInterval: "Hour",
  fromCount: [],
  fromCountCategories: [],
  fromDurr: [],
  fromDurrCategories: [],
  loading: false,
};

export default (state = initialState, action) => {
  switch (action.type) {
    case DATASOURCE.SUMMARY.KEY:
      let confData = getTopConferencesSeries(action.data.topConferences);
      let durData = getDurationSeries(action.data.durationBuckets, confData);
      let agentData = getAgentSeries(action.data.agents);
      let endReasons = getEndReasonSeries(action.data.endReasons);
      let activeLicenses = getActiveCallsPerHourSeries(action.data.maxLicensesPerInterval);
      let fromSeries = getFromSeries(action.data.fromDuration, action.data.total);
      return {
        ...state,
        loading: false,
        raw: action.data,
        topConferencesSeries: confData.series,
        topConferencesCategories: confData.categories,
        durationSeries: durData.series,
        durationCategories: durData.categories,
        agentSeries: agentData.series,
        agentDrilldown: agentData.drilldown,
        endReasonSeries: endReasons.series,
        endReasonDrilldown: endReasons.drilldown,
        activeCallsPerHourSeries: activeLicenses,
        timeInterval: action.data.timeInterval,
        fromCount: fromSeries.count,
        fromCountCategories: fromSeries.countCat,
        fromDurr: fromSeries.durr,
        fromDurrCategories: fromSeries.durrCat
      };
    case DATASOURCE.SUMMARY.LOADING:
      return {
        ...state,
        raw: {},
        durationSeries: [],
        durationCategories: [],
        topConferencesSeries: [],
        topConferencesCategories: [],
        agentSeries: [],
        agentDrilldown: [],
        agentCategories: [],
        endReasonSeries: [],
        activeCallsPerHourSeries: [],
        fromCount: [],
        fromCountCategories: [],
        fromDurr: [],
        fromDurrCategories: [],
        timeInterval: "Hour",
        loading: true
      };
    default:
      return state;
  }
};

function getFromSeries(fromData, totals) {
  if (!fromData || !fromData.buckets || fromData.buckets.length === 0) {
    return {series: [], categories: []};
  }

  let countSeries = {name: "Call Count", data: [], colorByPoint:true};
  let durrSeries = {name: "Endpoints by duration", data: [], colorByPoint:true};
  let countCategories = [];
  let durrCategories = [];
  fromData.buckets.forEach(bucket => {
    countSeries.data.push(bucket.doc_count);
    durrSeries.data.push(bucket.duration.value);
    countCategories.push(bucket.key);
    durrCategories.push(bucket.key);
  });

  return {count:[countSeries], countCat:countCategories, durr:[durrSeries], durrCat:durrCategories};
}

function getActiveCallsPerHourSeries(maxLicenses) {
  if (!maxLicenses || maxLicenses.length === 0) {
    return [];
  }

  let total = {name: "Total Joined", data: []};
  let max = {name: "Max Concurrent", data: []};

  maxLicenses.forEach(bucket => {
    total.data.push([bucket.key,bucket.doc_count]);
    // add one since the field is the max of tenantOtherActiveCallCount for the interval meaning it is not counting
    // itself, if undefined the answer is 0
    const myMax = bucket.maxLicenses.value !== undefined ? bucket.maxLicenses.value + 1 : 0;
    max.data.push([bucket.key,myMax]);
  });

  return [total,max];
}

function getEndReasonSeries(reasons) {
  let chartData = { series: [], drilldown: [] };
  if (!reasons || !reasons.buckets) {
    return chartData;
  }

  // create a mapping of devices so we can create the chart series
  let series = {};
  let total = reasons.sum_other_doc_count;
  reasons.buckets.forEach(o => {
    series[o.key] = {name: o.key, y: o.doc_count};
    total += o.doc_count;
  });


  // check how many options we have less than 1%
  let others = {};
  let otherCount = reasons.sum_other_doc_count;
  Object.keys(series).forEach( key => {
    if (series[key].y < 0.03 * total) {
      others[key] = series[key];
      otherCount += series[key].y;
    }
  });
  Object.keys(others).forEach( key => {
    delete series[key];
  });
  if (reasons.sum_other_doc_count > 0) {
    others.other = {name: "Not Aggregated", y: reasons.sum_other_doc_count};
  }
  if (otherCount > 0) {
    series.other = {name: "other", y: otherCount, drilldown: "other"};
  }

  // sort the data arrays
  let drilldown = [{name: "Other", id: "other", data: Object.values(others)}];
  chartData = Object.values(series);
  drilldown[0].data.sort(function(a,b) { return b.y-a.y });
  chartData.sort(function(a,b) { return b.y-a.y });

  return {series: [{name: "End Reasons", data: chartData, colorByPoint: true}], drilldown: drilldown}
}

function getAgentSeries(agents) {
  let chartData = { series: [], drilldown: [] };
  if (!agents || !agents.buckets) {
    return chartData;
  }

  // create a mapping of devices so we can create the chart series
  let series = {};
  let drilldown = {};
  if (agents.sum_other_doc_count > 0) {
    series.other = {name:"other", y:agents.sum_other_doc_count, id:"other"}
  }
  agents.buckets.forEach(o => {
    let versions = splitNameAndVersion(o.key);

    let name = versions[0];
    let prev = "";
    let fullname = name;

    if (!series.hasOwnProperty(fullname)) {
      series[fullname] = {name:name, y:0, id:fullname};
      if (versions.length > 1) {
        series[fullname].drilldown = fullname;
      }
    }
    series[fullname].y += o.doc_count;

    for (let jj = 1; jj< versions.length; jj++) {
      prev = fullname;
      name = versions[jj];
      fullname = prev + " - " + name;

      // this is a data object that goes in an object named after its parent. If it has
      // children, it needs a drilldown named after itself

      if (!drilldown.hasOwnProperty(prev)) {
        drilldown[prev] = {name:prev, id:prev, data:{}, colorByPoint: true};
      }

      if (!drilldown[prev].data.hasOwnProperty(name)) {
        drilldown[prev].data[name] = {name:name, y:0, id:name, colorByPoint: true}
        if (versions.length - 1 > jj) {
          drilldown[prev].data[name].drilldown = fullname;
        }
      }
      drilldown[prev].data[name].y += o.doc_count;
    }
  });

  // sort the data arrays
  let chartSeries = Object.values(series).sort(cmpVersions);
  let chartDrilldown = Object.values(drilldown);
  for (let ii = 0; ii < chartDrilldown.length; ii++) {
    chartDrilldown[ii].data = Object.values(chartDrilldown[ii].data).sort(cmpVersions);
  }

  return {series: [{name: "Devices", data: chartSeries, colorByPoint: true}], drilldown: chartDrilldown}
}

function getDurationSeries(buckets, confData) {
  let chartData = { series: [], categories: [] };
  if (!buckets) {
    return chartData;
  }

  let categories = ["1s","5s","30s","1m","5m","30m","1h","5h","30h","more"];
  let seriesMap = {};
  let series = [];

  confData.categories.forEach(conf => {
    seriesMap[conf] = series.push(
      {name: conf, data: Array(categories.length).fill(0) }
    )-1;
  });
  seriesMap["Other"] = series.push(
    {name: "Other", data: Array(categories.length).fill(0), color: "#777777" }
  )-1;
  let otherIdx = seriesMap["Other"];

  for (let ii = 0; ii < categories.length; ii++) {
    let c = categories[ii];
    if (!buckets[c]) {
      console.error("We're missing a bucket category [" + c + "]");
      return chartData;
    }
    series[otherIdx].data[ii] += buckets[c].conferences.sum_other_doc_count;

    buckets[c].conferences.buckets.forEach(confs => {
      let confIdx = seriesMap.hasOwnProperty(confs.key) ? seriesMap[confs.key] : otherIdx;
      series[confIdx].data[ii] += confs.doc_count;
    });
  };

  return {series: series, categories: categories}
}

function getTopConferencesSeries(conferences) {
  let series = {name:"Calls", data: [], colorByPoint: true};
  let categories = [];
  if (!conferences || conferences.length === 0) {
    return { series: [series], categories: categories};
  }

  let data = [];

  conferences.forEach(o => {
    data.push({name: o.key, total: o.doc_count});
  });

  if (conferences.length > 3) {
    data.sort((a,b) => b.total-a.total);
    let splitIdx = 0;
    for (let ii = 0; ii < data.length && ii < 8; ii++) {
      splitIdx++;
      if (ii > 3 && data[ii].total === 1) {
        break;
      }
    }

    if (splitIdx < data.length-1) {
      data = data.slice(0,splitIdx);
    }
  }

  data.forEach(d => {
    series.data.push(d.total);
    categories.push(d.name);
  });

  return { series: [series], categories: categories };
}
